<script setup lang="ts">
  // Date-range filter for the unified orders history: preset buttons apply
  // immediately; "Personalizado" uses two native <input type="date"> (zero
  // dependencies, platform pickers on touch) applied via Aplicar — same
  // pending-state interaction as the price popover.
  import { computed, getCurrentInstance, ref } from 'vue';
  import {
    DEFAULT_HISTORY_DATE_PRESET,
    getCustomRange,
    getPresetRange,
    parseLocalDayInput,
    toDateInputValue,
  } from '@/domain/pos/history-date-presets';
  import type {
    IHistoryDateRange,
    THistoryDatePreset,
  } from '@/domain/pos/history-date-presets';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';

  const props = defineProps<{
    range: IHistoryDateRange;
  }>();

  const emit = defineEmits<{
    (e: 'apply', value: IHistoryDateRange): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const PRESET_LABEL_KEYS: Record<THistoryDatePreset, string> = {
    today: 'posV2.historyDateToday',
    '7d': 'posV2.historyDate7d',
    '30d': 'posV2.historyDate30d',
    '90d': 'posV2.historyDate90d',
    custom: 'posV2.historyDateCustom',
  };

  const presets: Exclude<THistoryDatePreset, 'custom'>[] = [
    'today',
    '7d',
    '30d',
    '90d',
  ];

  const triggerLabel = computed(() =>
    i18nT(PRESET_LABEL_KEYS[props.range.preset])
  );

  const isNarrowed = computed(
    () => props.range.preset !== DEFAULT_HISTORY_DATE_PRESET
  );

  //#region Custom range (pending inputs → applied on "Aplicar")

  const pendingFrom = ref('');
  const pendingTo = ref('');

  const canApplyCustom = computed(
    () =>
      parseLocalDayInput(pendingFrom.value) != null &&
      parseLocalDayInput(pendingTo.value) != null
  );

  function syncPendingFromApplied(): void {
    if (props.range.preset === 'custom' && props.range.to) {
      pendingFrom.value = toDateInputValue(props.range.from);
      // Applied `to` is the exclusive next-midnight: show the inclusive day.
      const inclusiveTo = new Date(props.range.to);
      inclusiveTo.setDate(inclusiveTo.getDate() - 1);
      pendingTo.value = toDateInputValue(inclusiveTo);
    } else {
      pendingFrom.value = '';
      pendingTo.value = '';
    }
  }

  function handleToggle(open: boolean, toggle: () => void): void {
    if (!open) syncPendingFromApplied();
    toggle();
  }

  function applyPreset(
    preset: Exclude<THistoryDatePreset, 'custom'>,
    close: () => void
  ): void {
    emit('apply', getPresetRange(preset));
    close();
  }

  function applyCustom(close: () => void): void {
    const range = getCustomRange(pendingFrom.value, pendingTo.value);
    if (!range) return;
    emit('apply', range);
    close();
  }

  function reset(close: () => void): void {
    emit('apply', getPresetRange(DEFAULT_HISTORY_DATE_PRESET));
    close();
  }

  //#endregion
</script>

<template>
  <PosUiPopover placement="bottom" align="start">
    <template #trigger="{ open, toggle }">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="md"
        icon="calendar-range"
        class="date-trigger"
        @click="handleToggle(open, toggle)"
      >
        <span class="date-trigger-label">{{ triggerLabel }}</span>
        <span v-if="isNarrowed" class="date-active-dot" />
      </PosUiButton>
    </template>

    <template #default="{ close }">
      <div class="date-panel">
        <div class="preset-buttons">
          <button
            v-for="preset in presets"
            :key="preset"
            type="button"
            class="preset-button"
            :class="{ active: range.preset === preset }"
            @click="applyPreset(preset, close)"
          >
            {{ $t(PRESET_LABEL_KEYS[preset]) }}
          </button>
        </div>

        <div class="custom-section">
          <span class="custom-title">{{ $t('posV2.historyDateCustom') }}</span>
          <div class="custom-inputs">
            <div class="custom-field">
              <label class="custom-label">
                {{ $t('posV2.historyDateFrom') }}
              </label>
              <input v-model="pendingFrom" type="date" class="custom-input" />
            </div>
            <div class="custom-field">
              <label class="custom-label">
                {{ $t('posV2.historyDateTo') }}
              </label>
              <input v-model="pendingTo" type="date" class="custom-input" />
            </div>
          </div>
        </div>

        <div class="date-actions">
          <PosUiButton
            color="neutral"
            variant="ghost"
            size="sm"
            @click="reset(close)"
          >
            {{ $t('posV2.historyPriceReset') }}
          </PosUiButton>
          <PosUiButton
            color="primary"
            variant="solid"
            size="sm"
            :disabled="!canApplyCustom"
            @click="applyCustom(close)"
          >
            {{ $t('posV2.historyPriceApply') }}
          </PosUiButton>
        </div>
      </div>
    </template>
  </PosUiPopover>
</template>

<style scoped lang="scss">
  .date-trigger {
    @apply tw-relative;

    .date-trigger-label {
      @apply tw-hidden md:tw-inline;
    }

    .date-active-dot {
      @apply tw-absolute tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-orange-600;
    }
  }

  .date-panel {
    @apply tw-flex tw-w-64 tw-flex-col tw-gap-3;

    .preset-buttons {
      @apply tw-grid tw-grid-cols-2 tw-gap-1.5;

      .preset-button {
        @apply tw-min-h-[40px] tw-cursor-pointer tw-select-none tw-rounded-md tw-border tw-border-neutral-200 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-50 tw-text-neutral-800;
        }

        &.active {
          @apply tw-border-orange-300 tw-bg-orange-50 tw-font-semibold tw-text-orange-700;
        }
      }
    }

    .custom-section {
      @apply tw-flex tw-flex-col tw-gap-1.5 tw-border-t tw-border-neutral-100 tw-pt-3;

      .custom-title {
        @apply tw-text-xs tw-font-medium tw-text-neutral-500;
      }

      .custom-inputs {
        @apply tw-flex tw-gap-2;

        .custom-field {
          @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1;

          .custom-label {
            @apply tw-text-xs tw-text-neutral-400;
          }

          .custom-input {
            @apply tw-min-h-[40px] tw-w-full tw-rounded-md tw-border tw-border-neutral-200 tw-bg-white tw-px-2 tw-text-sm tw-text-neutral-700;

            &:focus {
              @apply tw-border-orange-400 tw-outline-none;
            }
          }
        }
      }
    }

    // Sticky within the scrolling popover panel (max-height can be under
    // the content height on short viewports): Aplicar must always be
    // visible. Negative offset because sticky respects the scroller's p-3.
    .date-actions {
      @apply tw-sticky tw--bottom-3 tw-z-10 tw--mx-3 tw--mb-3 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-100 tw-bg-white tw-px-3 tw-py-2 tw-shadow-[0_-4px_6px_-4px_rgba(15,23,42,0.12)];
    }
  }
</style>
